import { motion } from 'framer-motion'
import { AlertCircle, HelpCircle, Wrench, RefreshCw } from 'lucide-react'

const Troubleshooting = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <AlertCircle className="mr-3 text-accent-cyan" size={32} />
        问题排查
      </h1>

      {/* 常见问题 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">常见问题</h2>
        
        <div className="space-y-4">
          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between">
              <span className="flex items-center">
                <HelpCircle className="mr-2 text-accent-cyan" size={18} />
                无法连接到服务器
              </span>
              <span className="text-accent-cyan group-open:rotate-90 transition-transform">▶</span>
            </summary>
            <div className="mt-3 text-sm text-gray-400 pl-7 border-l-2 border-accent-cyan/30">
              <p className="mb-2"><strong className="text-white">可能原因：</strong></p>
              <ul className="list-disc ml-4 space-y-1 mb-3">
                <li>服务器未启动</li>
                <li>端口被占用</li>
                <li>防火墙拦截</li>
              </ul>
              <p className="mb-2"><strong className="text-white">解决方法：</strong></p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>检查服务器是否正在运行</li>
                <li>确认端口 8765 未被其他程序占用</li>
                <li>检查防火墙设置，允许 Avelia 通过</li>
                <li>尝试重启服务器和客户端</li>
              </ol>
            </div>
          </details>

          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between">
              <span className="flex items-center">
                <HelpCircle className="mr-2 text-accent-cyan" size={18} />
                AI 回复很慢或没有响应
              </span>
              <span className="text-accent-cyan group-open:rotate-90 transition-transform">▶</span>
            </summary>
            <div className="mt-3 text-sm text-gray-400 pl-7 border-l-2 border-accent-cyan/30">
              <p className="mb-2"><strong className="text-white">可能原因：</strong></p>
              <ul className="list-disc ml-4 space-y-1 mb-3">
                <li>模型服务响应慢（如 Ollama 本地模型）</li>
                <li>网络连接不稳定（使用在线 API 时）</li>
                <li>任务过于复杂</li>
              </ul>
              <p className="mb-2"><strong className="text-white">解决方法：</strong></p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>检查模型服务是否正常运行</li>
                <li>尝试切换到其他模型</li>
                <li>简化任务描述，分步骤执行</li>
                <li>检查网络连接</li>
                <li>如果长时间无响应，点击"停止"按钮重试</li>
              </ol>
            </div>
          </details>

          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between">
              <span className="flex items-center">
                <HelpCircle className="mr-2 text-accent-cyan" size={18} />
                没有可用的模型
              </span>
              <span className="text-accent-cyan group-open:rotate-90 transition-transform">▶</span>
            </summary>
            <div className="mt-3 text-sm text-gray-400 pl-7 border-l-2 border-accent-cyan/30">
              <p className="mb-2"><strong className="text-white">可能原因：</strong></p>
              <ul className="list-disc ml-4 space-y-1 mb-3">
                <li>未配置任何模型</li>
                <li>所有模型都被禁用</li>
                <li>当前模式没有对应类型的模型</li>
              </ul>
              <p className="mb-2"><strong className="text-white">解决方法：</strong></p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>打开设置窗口，进入"模型"页面</li>
                <li>添加至少一个聊天模型和一个推理模型</li>
                <li>确保模型已启用（开关打开）</li>
                <li>检查模型配置是否正确（API 地址、密钥等）</li>
              </ol>
            </div>
          </details>

          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between">
              <span className="flex items-center">
                <HelpCircle className="mr-2 text-accent-cyan" size={18} />
                文件上传失败
              </span>
              <span className="text-accent-cyan group-open:rotate-90 transition-transform">▶</span>
            </summary>
            <div className="mt-3 text-sm text-gray-400 pl-7 border-l-2 border-accent-cyan/30">
              <p className="mb-2"><strong className="text-white">可能原因：</strong></p>
              <ul className="list-disc ml-4 space-y-1 mb-3">
                <li>文件格式不支持</li>
                <li>文件过大</li>
                <li>文件路径包含特殊字符</li>
                <li>文件被其他程序占用</li>
              </ul>
              <p className="mb-2"><strong className="text-white">解决方法：</strong></p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>确认文件格式在支持列表中</li>
                <li>尝试压缩或分割大文件</li>
                <li>重命名文件，避免特殊字符</li>
                <li>关闭正在使用该文件的程序</li>
              </ol>
            </div>
          </details>

          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between">
              <span className="flex items-center">
                <HelpCircle className="mr-2 text-accent-cyan" size={18} />
                知识库检索不到内容
              </span>
              <span className="text-accent-cyan group-open:rotate-90 transition-transform">▶</span>
            </summary>
            <div className="mt-3 text-sm text-gray-400 pl-7 border-l-2 border-accent-cyan/30">
              <p className="mb-2"><strong className="text-white">可能原因：</strong></p>
              <ul className="list-disc ml-4 space-y-1 mb-3">
                <li>文档未正确导入</li>
                <li>关键词不匹配</li>
                <li>文档内容提取失败</li>
              </ul>
              <p className="mb-2"><strong className="text-white">解决方法：</strong></p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>在知识库页面确认文档已成功导入</li>
                <li>查看文档内容是否完整</li>
                <li>尝试使用不同的关键词提问</li>
                <li>如果是 PDF，确保不是扫描版（需要 OCR）</li>
              </ol>
            </div>
          </details>

          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between">
              <span className="flex items-center">
                <HelpCircle className="mr-2 text-accent-cyan" size={18} />
                语音识别不工作
              </span>
              <span className="text-accent-cyan group-open:rotate-90 transition-transform">▶</span>
            </summary>
            <div className="mt-3 text-sm text-gray-400 pl-7 border-l-2 border-accent-cyan/30">
              <p className="mb-2"><strong className="text-white">可能原因：</strong></p>
              <ul className="list-disc ml-4 space-y-1 mb-3">
                <li>麦克风权限未授予</li>
                <li>麦克风设备未正确连接</li>
                <li>语音服务未配置</li>
              </ul>
              <p className="mb-2"><strong className="text-white">解决方法：</strong></p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>检查浏览器/系统麦克风权限</li>
                <li>确认麦克风设备正常工作</li>
                <li>在系统设置中选择正确的输入设备</li>
                <li>检查语音服务配置</li>
              </ol>
            </div>
          </details>
        </div>
      </section>

      {/* 错误代码 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">常见错误代码</h2>
        
        <div className="glass-effect rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 font-semibold">错误代码</th>
                <th className="text-left p-3 font-semibold">说明</th>
                <th className="text-left p-3 font-semibold">解决方法</th>
              </tr>
            </thead>
            <tbody className="text-gray-400">
              <tr className="border-t border-white/5">
                <td className="p-3 font-mono text-red-400">401</td>
                <td className="p-3">未授权/需要登录</td>
                <td className="p-3 text-xs">检查用户登录状态，重新登录</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-mono text-red-400">403</td>
                <td className="p-3">权限不足</td>
                <td className="p-3 text-xs">检查权限设置，联系管理员</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-mono text-red-400">404</td>
                <td className="p-3">资源不存在</td>
                <td className="p-3 text-xs">确认文件路径或资源 ID 正确</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-mono text-red-400">500</td>
                <td className="p-3">服务器内部错误</td>
                <td className="p-3 text-xs">查看日志，重启服务器</td>
              </tr>
              <tr className="border-t border-white/5">
                <td className="p-3 font-mono text-red-400">TIMEOUT</td>
                <td className="p-3">请求超时</td>
                <td className="p-3 text-xs">检查网络连接，增加超时时间</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 性能优化 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">性能优化建议</h2>
        
        <div className="space-y-3">
          <div className="p-4 glass-effect rounded-lg border-l-4 border-blue-500">
            <h4 className="font-semibold mb-2 text-blue-300 flex items-center">
              <Wrench className="mr-2" size={18} />
              清理数据
            </h4>
            <p className="text-sm text-gray-400">
              定期清理不需要的记忆、知识库条目和日志文件，释放存储空间。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-blue-500">
            <h4 className="font-semibold mb-2 text-blue-300 flex items-center">
              <Wrench className="mr-2" size={18} />
              优化模型配置
            </h4>
            <p className="text-sm text-gray-400">
              使用本地模型时，根据硬件配置选择合适的模型大小。较小的模型响应更快。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-blue-500">
            <h4 className="font-semibold mb-2 text-blue-300 flex items-center">
              <Wrench className="mr-2" size={18} />
              减少并发任务
            </h4>
            <p className="text-sm text-gray-400">
              避免同时执行多个复杂任务，等待当前任务完成后再开始新任务。
            </p>
          </div>

          <div className="p-4 glass-effect rounded-lg border-l-4 border-blue-500">
            <h4 className="font-semibold mb-2 text-blue-300 flex items-center">
              <Wrench className="mr-2" size={18} />
              关闭不需要的功能
            </h4>
            <p className="text-sm text-gray-400">
              如果不使用语音、视觉等功能，可以在设置中关闭，减少资源占用。
            </p>
          </div>
        </div>
      </section>

      {/* 重置和重装 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">重置和重装</h2>
        
        <div className="space-y-4">
          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <RefreshCw className="mr-2 text-yellow-400" size={20} />
              重置配置
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              如果遇到配置问题，可以尝试重置：
            </p>
            <ol className="text-sm text-gray-400 space-y-2 ml-4 list-decimal">
              <li>关闭 Avelia</li>
              <li>找到配置文件目录（通常在用户目录下的 .avelia 文件夹）</li>
              <li>备份重要数据（知识库、记忆等）</li>
              <li>删除配置文件</li>
              <li>重新启动 Avelia，会自动生成默认配置</li>
            </ol>
          </div>

          <div className="glass-effect rounded-lg p-5">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <RefreshCw className="mr-2 text-red-400" size={20} />
              完全重装
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              如果问题仍然存在，可以完全重装：
            </p>
            <ol className="text-sm text-gray-400 space-y-2 ml-4 list-decimal">
              <li>导出重要数据（知识库、记忆、配置）</li>
              <li>卸载 Avelia</li>
              <li>删除所有相关文件和配置</li>
              <li>重新下载最新版本</li>
              <li>安装并导入之前的数据</li>
            </ol>
            <div className="mt-3 p-3 bg-red-500/10 rounded border border-red-500/30 text-xs">
              <p className="text-red-300">
                ⚠️ <strong>警告：</strong>重装会清除所有本地数据，请务必先备份！
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 获取帮助 */}
      <section className="mt-8 p-6 glass-effect rounded-lg border border-accent-cyan/30">
        <h3 className="text-xl font-semibold mb-4">💬 获取帮助</h3>
        <p className="text-sm text-gray-400 mb-4">
          如果以上方法都无法解决问题，可以通过以下方式获取帮助：
        </p>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">查看日志：</strong>在设置窗口的"日志"页面查看详细错误信息</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">社区论坛：</strong>在用户社区提问，其他用户可能遇到过类似问题</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">提交反馈：</strong>通过反馈窗口提交问题报告，包含详细的错误信息</span>
          </li>
          <li className="flex items-start">
            <span className="text-accent-cyan mr-2">•</span>
            <span><strong className="text-white">GitHub Issues：</strong>在项目的 GitHub 页面提交 Issue</span>
          </li>
        </ul>
      </section>
    </motion.div>
  )
}

export default Troubleshooting
